<template>
  <div class="internal-chat-panel">
    <div class="internal-chat-panel__header">
      <woot-thumbnail
        :src="conversation.other_user.avatar_url"
        :username="conversation.other_user.name"
        size="32px"
      />
      <h2 class="internal-chat-panel__title text-truncate">
        {{ conversation.other_user.name }}
      </h2>
    </div>

    <div v-if="conversation.readonly" class="internal-chat-panel__banner">
      <fluent-icon icon="warning" size="16" />
      <span>{{ $t('INTERNAL_CHAT.READONLY_BANNER') }}</span>
    </div>

    <div ref="messageList" class="internal-chat-panel__messages">
      <p
        v-if="isLoading && !messages.length"
        class="internal-chat-panel__empty"
      >
        {{ $t('INTERNAL_CHAT.MESSAGES_LOADING') }}
      </p>
      <p v-else-if="!messages.length" class="internal-chat-panel__empty">
        {{ $t('INTERNAL_CHAT.MESSAGES_EMPTY') }}
      </p>
      <message-bubble
        v-for="(message, index) in messages"
        :key="message.id"
        :message="message"
        :is-mine="isMine(message)"
        :sender="senderFor(message)"
        :show-avatar="shouldShowAvatar(message, index)"
      />
    </div>

    <div
      v-if="attachedFiles.length"
      class="internal-chat-panel__attachment-preview"
    >
      <attachment-preview
        :attachments="attachedFiles"
        :remove-attachment="removeAttachment"
      />
    </div>

    <div class="internal-chat-panel__composer">
      <woot-audio-recorder
        v-if="isRecordingAudio"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        @state-recorder-changed="onStateRecorderChanged"
        @finish-record="onFinishRecorder"
      />
      <textarea
        v-else
        ref="textarea"
        v-model="draftContent"
        class="internal-chat-panel__textarea"
        rows="1"
        :disabled="isComposerDisabled"
        :placeholder="$t('INTERNAL_CHAT.COMPOSER_PLACEHOLDER')"
        @keydown.enter.exact.prevent="handleSend"
      />

      <div class="internal-chat-panel__composer-actions">
        <div class="internal-chat-panel__emoji-wrap">
          <emoji-input
            v-if="showEmojiPicker"
            v-on-clickaway="hideEmojiPicker"
            :on-click="onEmojiClick"
          />
          <woot-button
            v-tooltip.top-end="$t('INTERNAL_CHAT.COMPOSER.EMOJI')"
            icon="emoji"
            color-scheme="secondary"
            variant="smooth"
            size="small"
            :disabled="isComposerDisabled"
            @click="toggleEmojiPicker"
          />
        </div>
        <file-upload
          ref="upload"
          v-tooltip.top-end="$t('INTERNAL_CHAT.COMPOSER.ATTACH')"
          :size="maxFileSizeInBytes"
          :multiple="true"
          :drop="true"
          :drop-directory="false"
          :disabled="isComposerDisabled"
          @input-file="onFileUpload"
        >
          <woot-button
            class-names="button--upload"
            icon="attach"
            color-scheme="secondary"
            variant="smooth"
            size="small"
            :disabled="isComposerDisabled"
          />
        </file-upload>
        <woot-button
          v-tooltip.top-end="$t('INTERNAL_CHAT.COMPOSER.AUDIO')"
          :icon="isRecordingAudio ? 'microphone-stop' : 'microphone'"
          :color-scheme="isRecordingAudio ? 'alert' : 'secondary'"
          variant="smooth"
          size="small"
          :disabled="conversation.readonly || isSending"
          @click="toggleAudioRecorder"
        />
        <woot-button
          class="internal-chat-panel__send-button"
          icon="send"
          :disabled="!canSend"
          @click="handleSend"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import FileUpload from 'vue-upload-component';
import alertMixin from 'shared/mixins/alertMixin';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import {
  MAXIMUM_FILE_UPLOAD_SIZE,
  AUDIO_FORMATS,
} from 'shared/constants/messages';
import MessageBubble from './MessageBubble.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import WootAudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

export default {
  components: {
    MessageBubble,
    EmojiInput,
    FileUpload,
    AttachmentPreview,
    WootAudioRecorder,
  },
  mixins: [clickaway, alertMixin],
  props: {
    conversation: {
      type: Object,
      required: true,
    },
    messages: {
      type: Array,
      default: () => [],
    },
    currentUserId: {
      type: Number,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draftContent: '',
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'internalChat/getUIFlags',
      currentUser: 'getCurrentUser',
    }),
    isSending() {
      return this.uiFlags.isSendingMessage;
    },
    isComposerDisabled() {
      return this.conversation.readonly || this.isSending;
    },
    maxFileSizeInBytes() {
      return MAXIMUM_FILE_UPLOAD_SIZE * 1024 * 1024;
    },
    audioRecordFormat() {
      return AUDIO_FORMATS.WAV;
    },
    canSend() {
      return (
        !this.isComposerDisabled &&
        (this.draftContent.trim().length > 0 || this.attachedFiles.length > 0)
      );
    },
  },
  watch: {
    messages() {
      this.scrollToBottom();
    },
    'conversation.id': function onConversationChange() {
      this.draftContent = '';
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.scrollToBottom();
    },
  },
  mounted() {
    this.scrollToBottom();
  },
  methods: {
    isMine(message) {
      return Number(message.sender_id) === Number(this.currentUserId);
    },
    senderFor(message) {
      return this.isMine(message)
        ? this.currentUser
        : this.conversation.other_user;
    },
    shouldShowAvatar(message, index) {
      const previous = this.messages[index - 1];
      return (
        !previous || Number(previous.sender_id) !== Number(message.sender_id)
      );
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messageList;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    async handleSend() {
      if (!this.canSend) {
        return;
      }

      const content = this.draftContent.trim();
      const files = this.attachedFiles.map(attachment => attachment.resource);
      this.draftContent = '';
      this.attachedFiles = [];

      try {
        await this.$store.dispatch('internalChat/sendMessage', {
          conversationId: this.conversation.id,
          content,
          files,
        });
      } catch (error) {
        this.draftContent = content;
      }
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      this.showEmojiPicker = false;
    },
    onEmojiClick(emoji) {
      const textarea = this.$refs.textarea;
      if (!textarea) {
        this.draftContent += emoji;
        return;
      }
      const { selectionStart, selectionEnd } = textarea;
      this.draftContent =
        this.draftContent.slice(0, selectionStart) +
        emoji +
        this.draftContent.slice(selectionEnd);
    },
    onFileUpload(file) {
      if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
        this.showAlert(
          this.$t('INTERNAL_CHAT.COMPOSER.FILE_SIZE_LIMIT', {
            maxSize: MAXIMUM_FILE_UPLOAD_SIZE,
          })
        );
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          resource: file.file,
          thumb: reader.result,
        });
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    toggleAudioRecorder() {
      if (this.isRecordingAudio) {
        if (this.$refs.audioRecorderInput) {
          this.$refs.audioRecorderInput.stopAudioRecording();
        }
        return;
      }
      this.isRecordingAudio = true;
    },
    onStateRecorderChanged(state) {
      if (state === 'notallowederror') {
        this.isRecordingAudio = false;
      }
    },
    onFinishRecorder(file) {
      if (file) {
        this.onFileUpload(file);
      }
      this.isRecordingAudio = false;
    },
  },
};
</script>

<style scoped lang="scss">
.internal-chat-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.internal-chat-panel__header {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-normal);
  border-bottom: 1px solid var(--color-border);
}

.internal-chat-panel__title {
  margin: 0;
  font-size: var(--font-size-medium);
}

.internal-chat-panel__banner {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  padding: var(--space-smaller) var(--space-normal);
  background: var(--y-50);
  color: var(--y-800);
  font-size: var(--font-size-mini);
}

.internal-chat-panel__messages {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-normal);
}

.internal-chat-panel__empty {
  color: var(--s-500);
  text-align: center;
  margin-top: var(--space-large);
}

.internal-chat-panel__attachment-preview {
  padding: 0 var(--space-normal);
  border-top: 1px solid var(--color-border);
}

.internal-chat-panel__composer {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  padding: var(--space-small) var(--space-normal);
  border-top: 1px solid var(--color-border);
}

.internal-chat-panel__textarea {
  resize: none;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: var(--space-smaller) var(--space-small);
  font-family: inherit;
  font-size: var(--font-size-default);
  max-height: 8rem;
  width: 100%;

  &:disabled {
    background: var(--color-background-light);
  }
}

.internal-chat-panel__composer-actions {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);

  .button {
    margin-bottom: 0;
  }
}

.internal-chat-panel__emoji-wrap {
  position: relative;

  ::v-deep .emoji-dialog {
    top: auto;
    right: auto;
    left: 0;
    bottom: calc(100% + var(--space-smaller));
  }
}

.internal-chat-panel__send-button {
  margin-left: auto;
}
</style>
